import React, { useState, useRef, useEffect } from 'react';
import { Box, IconButton, Paper, TextField, Tooltip } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import SendIcon from '@mui/icons-material/Send';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import PersonIcon from '@mui/icons-material/Person';
import LightbulbOutlinedIcon from '@mui/icons-material/LightbulbOutlined';

export interface AliModuleProps {
    isCompact?: boolean;
}

type MessageRole = 'user' | 'assistant';

interface Message {
    id: string;
    role: MessageRole;
    content: string;
    timestamp: Date;
}

const QUICK_PROMPTS = [
    { label: 'Resumen del pozo', prompt: 'Dame el resumen del pozo' },
    { label: 'Historial de producción', prompt: 'Dame el historial de producción' },
    { label: 'Últimos trabajos', prompt: 'Dame detalle del último trabajo' },
    { label: 'Recomendaciones', prompt: '¿Qué recomendaciones o diagnósticos necesito?' },
];

const DUMMY_RESPONSES: Record<string, string> = {
    resumen: 'Resumen del pozo 023-X:\n\nEl pozo opera en condiciones estables con aporte de fluidos. Nivel de líquido actual: 1,250 ft. Última carta dinagráfica (13-02-2026) indica condición "Aportando" con SPM 9.10 y eficiencia ~100%. Producción bruta reciente: 58.71 BPD.',
    produccion: 'Historial de producción:\n\n• Último mes: promedio 57 BPD\n• Tendencia: estable con ligera variación estacional\n• Pruebas de producción: 3 en los últimos 90 días\n• BSW último: 45%\n\nLos datos completos están en la pestaña Tendencias.',
    trabajo: 'Último trabajo de subsuelo:\n\n• Fecha: 15-01-2026\n• Tipo: Cambio de válvula viajera\n• Resultado: Exitoso\n• Días en operación: 34\n\nEl pozo retomó producción en 2 días. No se reportaron incidentes.',
    recomendacion: 'Recomendaciones sugeridas:\n\n1. Monitorear nivel de sumergencia (actualmente en rango óptimo)\n2. Próxima prueba de producción: en 30 días\n3. Revisar condición de fondo en próxima intervención\n4. Mantener SPM actual si la eficiencia se mantiene\n\nEste es un análisis de soporte. Consulte al ingeniero para decisiones finales.',
};

const getDummyResponse = (userMessage: string): string => {
    const lower = userMessage.trim().toLowerCase();
    if (lower.includes('resumen') || lower.includes('resume')) return DUMMY_RESPONSES.resumen;
    if (lower.includes('historial') || lower.includes('producción') || lower.includes('produccion')) return DUMMY_RESPONSES.produccion;
    if (lower.includes('último') || lower.includes('ultimo') || lower.includes('trabajo')) return DUMMY_RESPONSES.trabajo;
    if (lower.includes('recomendación') || lower.includes('recomendacion') || lower.includes('diagnóstico') || lower.includes('diagnostico')) return DUMMY_RESPONSES.recomendacion;
    return 'He recibido tu consulta. En la versión completa, ALI analizaría el historial del pozo y proporcionaría una respuesta personalizada.';
};

export const AliModule = ({ isCompact = true }: AliModuleProps) => {
    const [messages, setMessages] = useState<Message[]>([
        {
            id: '0',
            role: 'assistant',
            content: 'Hola, soy ALI. Puedo ayudarte con el historial del pozo. Usa los botones de abajo o escribe tu pregunta.',
            timestamp: new Date(),
        },
    ]);
    const [inputValue, setInputValue] = useState('');
    const [isTyping, setIsTyping] = useState(false);
    const scrollRef = useRef<HTMLDivElement>(null);

    useEffect(() => {
        scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: 'smooth' });
    }, [messages, isTyping]);

    const sendMessage = (text: string) => {
        const trimmed = text.trim();
        if (!trimmed) return;

        const userMsg: Message = {
            id: `u-${Date.now()}`,
            role: 'user',
            content: trimmed,
            timestamp: new Date(),
        };
        setMessages((prev) => [...prev, userMsg]);
        setInputValue('');
        setIsTyping(true);

        setTimeout(() => {
            const response = getDummyResponse(trimmed);
            const botMsg: Message = {
                id: `b-${Date.now()}`,
                role: 'assistant',
                content: response,
                timestamp: new Date(),
            };
            setMessages((prev) => [...prev, botMsg]);
            setIsTyping(false);
        }, 800 + Math.random() * 600);
    };

    const handleSend = () => {
        sendMessage(inputValue);
    };

    const handleKeyDown = (e: React.KeyboardEvent) => {
        if (e.key === 'Enter' && !e.shiftKey) {
            e.preventDefault();
            handleSend();
        }
    };

    const resetChat = () => {
        setMessages([
            { id: '0', role: 'assistant', content: 'Hola, soy ALI. Puedo ayudarte con el historial del pozo. Usa los botones de abajo o escribe tu pregunta.', timestamp: new Date() },
        ]);
        setInputValue('');
        setIsTyping(false);
    };

    return (
        <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%', overflow: 'hidden', bgcolor: 'grey.50' }}>
            {/* Barra superior: ALI + Reiniciar */}
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', px: 1.5, py: 0.75, borderBottom: '1px solid', borderColor: 'divider', bgcolor: 'white', flexShrink: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <SmartToyIcon sx={{ fontSize: 18, color: 'primary.main' }} />
                    <Box component="span" sx={{ fontSize: isCompact ? 12 : 13, fontWeight: 700, color: 'text.primary' }}>ALI</Box>
                </Box>
                <Tooltip title="Reiniciar conversación">
                    <IconButton size="small" onClick={resetChat} disabled={isTyping} aria-label="Reiniciar chat">
                        <RefreshIcon fontSize="small" />
                    </IconButton>
                </Tooltip>
            </Box>
            {/* Área de chat con scroll */}
            <Box
                ref={scrollRef}
                sx={{
                    flex: 1,
                    overflowY: 'auto',
                    p: 1.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.5,
                }}
            >
                {messages.map((msg) => (
                    <Box
                        key={msg.id}
                        sx={{
                            display: 'flex',
                            flexDirection: msg.role === 'user' ? 'row-reverse' : 'row',
                            gap: 1,
                            alignSelf: msg.role === 'user' ? 'flex-end' : 'flex-start',
                            maxWidth: '90%',
                        }}
                    >
                        <Box
                            sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                flexShrink: 0,
                                bgcolor: msg.role === 'user' ? 'primary.main' : 'grey.300',
                                color: msg.role === 'user' ? 'white' : 'grey.700',
                            }}
                        >
                            {msg.role === 'user' ? <PersonIcon sx={{ fontSize: 14 }} /> : <SmartToyIcon sx={{ fontSize: 14 }} />}
                        </Box>
                        <Paper
                            elevation={0}
                            sx={{
                                p: 1.25,
                                borderRadius: 2,
                                bgcolor: msg.role === 'user' ? 'primary.light' : 'white',
                                color: msg.role === 'user' ? 'primary.contrastText' : 'text.primary',
                                border: '1px solid',
                                borderColor: msg.role === 'user' ? 'primary.main' : 'grey.200',
                                fontSize: isCompact ? 12 : 13,
                                whiteSpace: 'pre-wrap',
                                wordBreak: 'break-word',
                            }}
                        >
                            {msg.content}
                        </Paper>
                    </Box>
                ))}
                {isTyping && (
                    <Box sx={{ display: 'flex', gap: 1, alignSelf: 'flex-start' }}>
                        <Box sx={{ width: 24, height: 24, borderRadius: '50%', bgcolor: 'grey.300', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <SmartToyIcon sx={{ fontSize: 14, color: 'grey.700' }} />
                        </Box>
                        <Paper elevation={0} sx={{ p: 1.25, borderRadius: 2, bgcolor: 'white', border: '1px solid', borderColor: 'grey.200' }}>
                            <Box sx={{ display: 'flex', gap: 0.5 }}>
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400', animation: 'aliPulse 0.8s ease-in-out infinite' }} />
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400', animation: 'aliPulse 0.8s ease-in-out 0.2s infinite' }} />
                                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: 'grey.400', animation: 'aliPulse 0.8s ease-in-out 0.4s infinite' }} />
                            </Box>
                        </Paper>
                    </Box>
                )}
            </Box>

            {/* Sugerencias rápidas */}
            <Box sx={{ px: 1.5, pb: 0.5, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {QUICK_PROMPTS.map(({ label, prompt }) => (
                    <Paper
                        key={prompt}
                        component="button"
                        type="button"
                        elevation={0}
                        onClick={() => sendMessage(prompt)}
                        sx={{
                            px: 1,
                            py: 0.5,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'grey.200',
                            bgcolor: 'white',
                            cursor: 'pointer',
                            fontSize: isCompact ? 10 : 11,
                            fontWeight: 600,
                            color: 'text.secondary',
                            textAlign: 'left',
                            '&:hover': { bgcolor: 'primary.50', borderColor: 'primary.light', color: 'primary.main' },
                            transition: 'all 0.15s',
                        }}
                    >
                        {label}
                    </Paper>
                ))}
            </Box>

            {/* Panel fijo inferior */}
            <Box
                sx={{
                    flexShrink: 0,
                    p: 1,
                    pt: 0.5,
                    borderTop: '1px solid',
                    borderColor: 'divider',
                    bgcolor: 'white',
                    display: 'flex',
                    alignItems: 'flex-end',
                    gap: 0.5,
                }}
            >
                <IconButton
                    size="small"
                    onClick={() => sendMessage(QUICK_PROMPTS[0].prompt)}
                    sx={{ flexShrink: 0 }}
                    title="Dame el resumen del pozo"
                >
                    <LightbulbOutlinedIcon fontSize="small" color="primary" />
                </IconButton>
                <TextField
                    size="small"
                    placeholder="Escribe tu pregunta sobre el pozo..."
                    value={inputValue}
                    onChange={(e) => setInputValue(e.target.value)}
                    onKeyDown={handleKeyDown}
                    disabled={isTyping}
                    fullWidth
                    multiline
                    maxRows={3}
                    sx={{
                        '& .MuiOutlinedInput-root': { fontSize: isCompact ? 12 : 13 },
                    }}
                />
                <IconButton
                    size="small"
                    color="primary"
                    onClick={handleSend}
                    disabled={!inputValue.trim() || isTyping}
                >
                    <SendIcon fontSize="small" />
                </IconButton>
            </Box>

            <style>{`
                @keyframes aliPulse {
                    0%, 100% { opacity: 0.4; }
                    50% { opacity: 1; }
                }
            `}</style>
        </Box>
    );
};
